import { useEffect, useState } from "react";
import { collection, query, where, orderBy, limit, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import type { Interaction } from "../types";

export interface InteractionWithId extends Interaction {
  id: string;
}

/**
 * Real-time listener for the current user's most recently created interactions
 * across all trips. Used on the home page to figure out which trips have
 * activity happening right now (so the "current trip" detection isn't purely
 * date-based).
 */
/** The last answer per user and size, so Today comes back already drawn. */
const lastSeen = new Map<string, InteractionWithId[]>();

export function useRecentInteractions(max = 50) {
  const { user } = useAuth();
  const key = user ? `${user.uid}:${max}` : null;
  const [interactions, setInteractions] = useState<InteractionWithId[]>(
    () => (key ? lastSeen.get(key) : undefined) ?? [],
  );
  const [loading, setLoading] = useState(() => !(key && lastSeen.has(key)));

  useEffect(() => {
    if (!user) {
      setInteractions([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "interactions"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc"),
      limit(max),
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as InteractionWithId[];
      lastSeen.set(`${user.uid}:${max}`, data);
      setInteractions(data);
      setLoading(false);
    });

    return unsub;
  }, [user, max]);

  return { interactions, loading };
}
